// franklycatering #1557 — what the Events board's buttons do, and the state of
// the dialogs they open. Handlers are stable (useCallback, one memoized
// object), so memoized cards don't re-render when a dialog opens. The UI
// renders the dialogs from `dialogs`; nothing here renders.

import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EVENT_STATUS_LABELS, useUpdateEventStatus } from "@/hooks/useEvents";
import {
  invalidateEventViews,
  useCreateEventQuote,
  useMarkEventDone,
  useMarkEventWrapUp,
  type CreatedEventQuote,
  type WrapUpField,
} from "@/hooks/useEventsPipeline";
import { dropTarget } from "@/lib/event-pipeline";
import { reportUpdateFailure } from "@/lib/event-update-failure";
import type { BoardRow } from "@/lib/event-board";

export interface PaymentTarget {
  quoteId: string;
  quoteTotal: number;
  amountPaid: number;
  quoteNumber: string;
}

export interface EventBoardHandlers {
  /** A quote line: open that quote in the side panel. */
  openQuote: (quoteId: string) => void;
  /** "Add quote": a draft on the event, opened in the quote drawer. */
  addQuote: (eventId: string) => void;
  markLost: (row: BoardRow) => void;
  /** Asks first: a cancelled event can't be reopened from the board. */
  cancelEvent: (row: BoardRow) => void;
  /** Record payment on owingQuote(); nothing when nothing is owed. */
  recordPayment: (row: BoardRow) => void;
  markSent: (row: BoardRow, field: WrapUpField) => void;
  /** Asks first, then sets the event completed. */
  markDone: (row: BoardRow) => void;
  /** A List row: the event's detail sheet. */
  openDetail: (eventId: string) => void;
  /** "+ New event": the New quote dialog, which makes the event; the drawer then opens on the draft. */
  newEvent: () => void;
}

export interface EventBoardPending {
  /** "<event id>:<field>" while a Mark sent is saving. */
  wrapUp: string | null;
  /** Event id while Mark done is saving. */
  done: string | null;
  /** Event id while an Add quote draft is being made. */
  addQuote: string | null;
}

export interface EventBoardDialogState {
  quote: { quoteId: string | null; draft: CreatedEventQuote | null; onClose: () => void };
  cancel: { row: BoardRow | null; onConfirm: () => void; onDismiss: () => void };
  done: { row: BoardRow | null; onConfirm: () => void; onDismiss: () => void };
  payment: { target: PaymentTarget | null; onClose: () => void };
  newEvent: { open: boolean; onClose: () => void; onCreated: (quoteId: string) => void };
  detail: { eventId: string | null; onClose: () => void };
}

const WRAP_UP_TOASTS: Record<WrapUpField, string> = {
  thank_you_sent_at: "Thank-you marked sent.",
  review_requested_at: "Review ask marked sent.",
};

/** Mark lost and Cancel event (moved from the old drag-and-drop board). */
function useStatusMoves() {
  // mutateAsync is stable across renders; the mutation object is not.
  const { mutateAsync: updateStatus } = useUpdateEventStatus();
  const [confirmCancel, setConfirmCancel] = useState<BoardRow | null>(null);

  const applyStatus = useCallback(
    async (row: BoardRow, status: "lost" | "cancelled") => {
      const event = row.event;
      try {
        await updateStatus({ id: event.id, fromStatus: event.status, status });
        toast.success(`${event.event_number} marked ${EVENT_STATUS_LABELS[status].toLowerCase()}`);
      } catch (err) {
        reportUpdateFailure(err, "Failed to update status", "status", event.id);
      }
    },
    [updateStatus],
  );

  const requestMove = useCallback(
    (row: BoardRow, to: "lost" | "cancelled") => {
      const result = dropTarget(row.event.status, row.stage, to, row.event.event_number);
      if (!result.ok) {
        if (result.reason) toast.error(result.reason);
        return;
      }
      // Cancelled can't be undone from the UI, so confirm it first.
      if (result.status === "cancelled") setConfirmCancel(row);
      else void applyStatus(row, "lost");
    },
    [applyStatus],
  );

  const markLost = useCallback((row: BoardRow) => requestMove(row, "lost"), [requestMove]);
  const cancelEvent = useCallback((row: BoardRow) => requestMove(row, "cancelled"), [requestMove]);
  const confirmCancelEvent = useCallback(() => {
    if (confirmCancel) void applyStatus(confirmCancel, "cancelled");
    setConfirmCancel(null);
  }, [confirmCancel, applyStatus]);
  const dismissCancel = useCallback(() => setConfirmCancel(null), []);

  return { markLost, cancelEvent, confirmCancel, confirmCancelEvent, dismissCancel };
}

/** The Post-event checklist: Mark sent and Mark done. */
function useWrapUpActions() {
  const wrapUp = useMarkEventWrapUp();
  const done = useMarkEventDone();
  const { mutate: markWrapUp } = wrapUp;
  const { mutate: markEventDone } = done;
  const [confirmDone, setConfirmDone] = useState<BoardRow | null>(null);

  const markSent = useCallback(
    (row: BoardRow, field: WrapUpField) => {
      markWrapUp(
        { id: row.id, field },
        { onSuccess: (changed) => toast.success(changed ? WRAP_UP_TOASTS[field] : "Already marked sent.") },
      );
    },
    [markWrapUp],
  );

  const markDone = useCallback((row: BoardRow) => setConfirmDone(row), []);
  const confirmMarkDone = useCallback(() => {
    const row = confirmDone;
    setConfirmDone(null);
    if (!row) return;
    markEventDone(
      { id: row.id },
      { onSuccess: () => toast.success(`${row.name} is wrapped up and moved to Done. Pick Done in Event status to see it.`) },
    );
  }, [confirmDone, markEventDone]);
  const dismissDone = useCallback(() => setConfirmDone(null), []);

  const pendingWrapUp = wrapUp.isPending && wrapUp.variables ? `${wrapUp.variables.id}:${wrapUp.variables.field}` : null;
  const pendingDone = done.isPending && done.variables ? done.variables.id : null;
  return { markSent, markDone, confirmDone, confirmMarkDone, dismissDone, pendingWrapUp, pendingDone };
}

/** The quote panel: open a quote, or make a draft and open the form on it. */
function useQuotePanel() {
  const create = useCreateEventQuote();
  const { mutate: createQuote } = create;
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CreatedEventQuote | null>(null);

  const openQuote = useCallback((id: string) => setQuoteId(id), []);
  const addQuote = useCallback(
    (eventId: string) => createQuote({ eventId }, { onSuccess: (d) => setDraft(d) }),
    [createQuote],
  );
  const closeQuote = useCallback(() => {
    setQuoteId(null);
    setDraft(null);
  }, []);
  const pendingAdd = create.isPending && create.variables ? create.variables.eventId : null;
  return { quoteId, draft, openQuote, addQuote, closeQuote, pendingAdd };
}

/** Record payment, + New event and the List row's detail sheet. */
function useSimpleDialogs() {
  const qc = useQueryClient();
  const [payment, setPayment] = useState<PaymentTarget | null>(null);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const recordPayment = useCallback((row: BoardRow) => {
    const q = row.owingQuote;
    if (!q) return;
    setPayment({ quoteId: q.id, quoteTotal: Number(q.total), amountPaid: Number(q.amount_paid), quoteNumber: q.quote_number });
  }, []);
  // RecordPaymentDialog only refreshes ["quotes"]; the board and event pages need it too.
  const closePayment = useCallback(() => {
    setPayment(null);
    invalidateEventViews(qc);
  }, [qc]);
  const newEvent = useCallback(() => setNewEventOpen(true), []);
  const closeNewEvent = useCallback(() => {
    setNewEventOpen(false);
    qc.invalidateQueries({ queryKey: ["events"] });
  }, [qc]);
  const openDetail = useCallback((id: string) => setDetailId(id), []);
  const closeDetail = useCallback(() => setDetailId(null), []);

  return { payment, recordPayment, closePayment, newEventOpen, newEvent, closeNewEvent, detailId, openDetail, closeDetail };
}

export function useEventBoardActions(): {
  handlers: EventBoardHandlers;
  pending: EventBoardPending;
  dialogs: EventBoardDialogState;
} {
  const moves = useStatusMoves();
  const wrap = useWrapUpActions();
  const panel = useQuotePanel();
  const simple = useSimpleDialogs();

  const handlers = useMemo<EventBoardHandlers>(
    () => ({
      openQuote: panel.openQuote,
      addQuote: panel.addQuote,
      markLost: moves.markLost,
      cancelEvent: moves.cancelEvent,
      recordPayment: simple.recordPayment,
      markSent: wrap.markSent,
      markDone: wrap.markDone,
      openDetail: simple.openDetail,
      newEvent: simple.newEvent,
    }),
    [
      panel.openQuote,
      panel.addQuote,
      moves.markLost,
      moves.cancelEvent,
      simple.recordPayment,
      wrap.markSent,
      wrap.markDone,
      simple.openDetail,
      simple.newEvent,
    ],
  );

  const pending = useMemo<EventBoardPending>(
    () => ({ wrapUp: wrap.pendingWrapUp, done: wrap.pendingDone, addQuote: panel.pendingAdd }),
    [wrap.pendingWrapUp, wrap.pendingDone, panel.pendingAdd],
  );

  const { closeNewEvent } = simple;
  const { openQuote } = panel;
  const onNewQuoteCreated = useCallback(
    (id: string) => {
      closeNewEvent();
      openQuote(id);
    },
    [closeNewEvent, openQuote],
  );

  const dialogs: EventBoardDialogState = {
    quote: { quoteId: panel.quoteId, draft: panel.draft, onClose: panel.closeQuote },
    cancel: { row: moves.confirmCancel, onConfirm: moves.confirmCancelEvent, onDismiss: moves.dismissCancel },
    done: { row: wrap.confirmDone, onConfirm: wrap.confirmMarkDone, onDismiss: wrap.dismissDone },
    payment: { target: simple.payment, onClose: simple.closePayment },
    newEvent: { open: simple.newEventOpen, onClose: simple.closeNewEvent, onCreated: onNewQuoteCreated },
    detail: { eventId: simple.detailId, onClose: simple.closeDetail },
  };

  return { handlers, pending, dialogs };
}
